import { useState, useEffect } from 'react';
import authService from '../services/authService';

/**
 * Custom React Hook for Managing User Authentication State
 */
export const useAuth = () => {
  const [user, setUser] = useState<any>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const cachedUser = authService.getCurrentUser();
    const authenticated = authService.isAuthenticated();
    setUser(cachedUser);
    setIsAuthenticated(authenticated);
    setLoading(false);
  }, []);

  /**
   * Triggers credentials validation and hydrates session state on success
   */
  const login = async (email: string, password: string): Promise<any> => {
    setLoading(true);
    try {
      const data = await authService.login(email, password);
      if (data.success) {
        setUser(data.data.user);
        setIsAuthenticated(true);
      }
      return data;
    } catch (error: any) {
      setIsAuthenticated(false);
      setUser(null);
      throw error.response?.data?.message || 'Login failed due to connection error';
    } finally {
      setLoading(false);
    }
  };

  /**
   * Discards authentication context and logs out
   */
  const logout = (): void => {
    authService.logout();
    setUser(null);
    setIsAuthenticated(false);
  };

  /**
   * Dynamically checks if the user possesses a specific permission
   */
  const hasPermission = (permission: string): boolean => {
    return user?.permissions?.includes(permission) || false;
  };

  /**
   * Dynamically checks if the user possesses a specific role
   */
  const hasRole = (role: string): boolean => {
    return user?.roles?.includes(role) || false;
  };

  return { 
    user, 
    isAuthenticated, 
    loading, 
    login, 
    logout,
    hasPermission,
    hasRole
  };
};

export default useAuth;
